import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ArrowUpIcon } from '@heroicons/react/24/outline';
import { getSubscription } from '../services/paymentService';

const UsageIndicator = ({ compact = false }) => {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await getSubscription();
      if (response.success) {
        setUsage(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usage) {
    return null;
  }

  // Pro users don't need to see usage
  if (usage.plan === 'pro') {
    if (compact) return null;
    return (
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-3 text-white text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium">Pro Plan</span>
          <span className="text-primary-100">Unlimited access</span>
        </div>
      </div>
    );
  }

  const { limits } = usage;
  const jobsUsed = usage.usage?.jobsCapturedThisWeek || 0;
  const tailorsUsed = usage.usage?.tailorsThisMonth || 0;
  const jobsLimit = limits?.jobCapturesPerWeek || 3;
  const tailorsLimit = limits?.tailorsPerMonth || 2;

  const jobsPercent = Math.min(100, (jobsUsed / jobsLimit) * 100);
  const tailorsPercent = Math.min(100, (tailorsUsed / tailorsLimit) * 100);

  if (compact) {
    return (
      <div className="space-y-2 px-3 py-2">
        <div className="flex justify-between text-xs text-gray-500">
          <span>Jobs: {jobsUsed}/{jobsLimit}</span>
          <span>Tailors: {tailorsUsed}/{tailorsLimit}</span>
        </div>
        <button
          onClick={() => navigate('/pricing')}
          className="w-full text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center gap-1"
        >
          <ArrowUpIcon className="h-3 w-3" />
          Upgrade for unlimited
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900">Free Plan Usage</h3>
        <button
          onClick={() => navigate('/pricing')}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          <ArrowUpIcon className="h-3 w-3" />
          Upgrade
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Job Captures</span>
            <span className="font-medium text-gray-900">{jobsUsed} / {jobsLimit} this week</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                jobsPercent >= 100 ? 'bg-red-500' :
                jobsPercent >= 66 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${jobsPercent}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Resume Tailoring</span>
            <span className="font-medium text-gray-900">{tailorsUsed} / {tailorsLimit} this month</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                tailorsPercent >= 100 ? 'bg-red-500' :
                tailorsPercent >= 50 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${tailorsPercent}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

UsageIndicator.propTypes = {
  compact: PropTypes.bool
};

export default UsageIndicator;
